import { IReviewWorkoutDetails, TimeSpan } from 'fitbeat.models';
import { INotificationService } from 'fitbeat.notification-service';
import { IUserRating, UserWorkoutSession } from '../../entities';
import { IWorkoutRepository } from '../../persistence';
import { IUserReview } from './iuserreview';

export class UserReview implements IUserReview {
    private workoutRepository: IWorkoutRepository;

    constructor(workoutRepo: IWorkoutRepository) {
        this.workoutRepository = workoutRepo;
    }

    public async getLastWorkoutToBeReviewed(userProfileId: string, now: Date): Promise<UserWorkoutSession | undefined> {
        const aWeekAgo = TimeSpan.getAWeekAgo(now);
        const lastworkout = await this.workoutRepository.getLastWorkout(userProfileId, aWeekAgo, now);
        if (this.hasWorkoutBeenReviewed(lastworkout)) {
            return undefined;
        }
        return lastworkout;
    }

    public async addWorkoutReview(
        userWorkoutSessionId: string,
        userProfileId: string,
        rating?: number,
        comment?: string,
    ): Promise<IUserRating> {
        const userWorkoutSession = await this.workoutRepository.getWorkoutSessionFromId(userWorkoutSessionId);
        if (!userWorkoutSession || !this.isRequestingOwner(userWorkoutSession, userProfileId)) {
            throw new Error(`No workout found with id: ${userWorkoutSessionId}`);
        }

        const userRating = this.createUserReviewEntry(rating, comment);
        userWorkoutSession.userRating = userRating;
        await this.workoutRepository.update(userWorkoutSession);
        return userRating;
    }

    public async sendReviewLastWorkoutNotifications(notificationService: INotificationService,
                                                    now: Date): Promise<string[]> {
      const sessionsToNotify = await this.workoutRepository.getWorkoutSessionToNotifyForReview(now);

      const sendNotification = async (session: UserWorkoutSession) => {
        const details: IReviewWorkoutDetails = {
          workoutUserId: session.userProfileId,
        };
        const result = await notificationService.reviewLastWorkout(details);

        if (result) {
          try {
            const current = await this.workoutRepository.getWorkoutSessionFromId(session.uniqueReference);
            if (current === undefined) {
              throw new Error('failed to load session after id retrieved (this should not happen)');
            }
            current.reviewNotified = true;
            await this.workoutRepository.update(current);
          } catch (e) {
          //  Ignore
          }
        }

        return result;
      };

      const notifications = sessionsToNotify.map((session) => sendNotification(session));
      return await Promise.all(notifications);
    }

    private createUserReviewEntry(rating?: number, comment?: string): IUserRating {
        const skipped = rating === undefined;
        return {
            date: new Date(),
            rating,
            comment,
            skipped,
        };
    }

    private hasWorkoutBeenReviewed(workout: UserWorkoutSession | undefined): boolean {
        if (workout && workout.userRating) {
            return true;
        }
        return false;
    }

    private isRequestingOwner(userWorkoutSession: UserWorkoutSession, userProfileId: string): boolean {
        return userWorkoutSession.userProfileId === userProfileId;
    }
}
